// Constants

export const VENDOR_CREATION_MESSAGE = "To get started listing apps on Marketplace, you'll need to create a vendor profile. This information is publicly visible on Marketplace.";
export const VALID_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg'];
export const MAX_DESCRIPTION_LENGTH = 750;
export const MIN_SUPPORT_DURATION_MINUTES = 60;
export const validateImageFile = fileName => {
  const extension = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
  if (extension && !VALID_IMAGE_EXTENSIONS.includes(extension)) {
    return "".concat(fileName, " has an invalid extension. Valid extension(s): ").concat(VALID_IMAGE_EXTENSIONS.join(', '), ".");
  }
  return undefined;
};
export const validateDescription = value => {
  if (value && value.length > MAX_DESCRIPTION_LENGTH) {
    return "Description is greater than ".concat(MAX_DESCRIPTION_LENGTH, " characters.");
  }
  return undefined;
};

// File handling utilities
export const createImagePreview = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      var _e$target;
      resolve((_e$target = e.target) === null || _e$target === void 0 ? void 0 : _e$target.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};
export const REGION_OPTIONS = [{
  label: 'Africa',
  value: 'Africa'
}, {
  label: 'America',
  value: 'America'
}, {
  label: 'Antarctica',
  value: 'Antarctica'
}, {
  label: 'Asia',
  value: 'Asia'
}, {
  label: 'Atlantic',
  value: 'Atlantic'
}, {
  label: 'Australia',
  value: 'Australia'
}, {
  label: 'Europe',
  value: 'Europe'
}, {
  label: 'Indian',
  value: 'Indian'
}, {
  label: 'Pacific',
  value: 'Pacific'
}];
export const SUPPORT_DAYS = [{
  key: 'Monday',
  name: 'supportDayMonday'
}, {
  key: 'Tuesday',
  name: 'supportDayTuesday'
}, {
  key: 'Wednesday',
  name: 'supportDayWednesday'
}, {
  key: 'Thursday',
  name: 'supportDayThursday'
}, {
  key: 'Friday',
  name: 'supportDayFriday'
}, {
  key: 'Saturday',
  name: 'supportDaySaturday'
}, {
  key: 'Sunday',
  name: 'supportDaySunday'
}];

// Hour and minute options for support time selects
export const HOUR_OPTIONS = Array.from({
  length: 24
}).map((_, i) => {
  const value = String(i).padStart(2, '0');
  return {
    label: value,
    value
  };
});
export const MINUTE_OPTIONS = [{
  label: '00',
  value: '00'
}, {
  label: '30',
  value: '30'
}];

// Placeholder options for selects
export const REGION_PLACEHOLDER_OPTION = {
  label: 'Select a region',
  value: ''
};
export const TIMEZONE_PLACEHOLDER_OPTION = {
  label: 'Select a time zone',
  value: ''
};

// Conditional validators for Contact Address fields

const isNonBlank = v => !!v && v.trim() !== '';
export const isAddressGroupFilled = formState => {
  const {
    addressLine1,
    addressLine2,
    city,
    state,
    postalCode,
    country
  } = formState || {};
  return [addressLine1, addressLine2, city, state, postalCode].some(isNonBlank) || Boolean(country);
};
export const validateAddressLine1 = (value, formState) => {
  return isAddressGroupFilled(formState) && !isNonBlank(value) ? 'Address Line 1 cannot be blank' : undefined;
};
export const validateCity = (value, formState) => {
  return isAddressGroupFilled(formState) && !isNonBlank(value) ? 'City is required' : undefined;
};
export const validateState = (value, formState) => {
  return isAddressGroupFilled(formState) && !isNonBlank(value) ? 'State is required' : undefined;
};
export const validatePostalCode = (value, formState) => {
  return isAddressGroupFilled(formState) && !isNonBlank(value) ? 'Postal Code is required' : undefined;
};
export const validateCountry = (value, formState) => {
  return isAddressGroupFilled(formState) && !value ? 'Country is required' : undefined;
};

// Local types for support hours validation

// Support hours validation
export const validateSupportUntilTime = (_value, formState) => {
  const state = formState;
  const isTwentyFourHourSupport = Boolean(state === null || state === void 0 ? void 0 : state.twentyFourHourSupport);
  if (isTwentyFourHourSupport) {
    return undefined;
  }
  const toNumber = v => {
    if (v == null) return NaN;
    const raw = typeof v === 'object' ? v.value : v;
    const num = Number(raw);
    return Number.isFinite(num) ? num : NaN;
  };
  const fromHour = toNumber(state === null || state === void 0 ? void 0 : state.supportHoursOfOperationFromHour);
  const fromMinute = toNumber(state === null || state === void 0 ? void 0 : state.supportHoursOfOperationFromMinute);
  const untilHour = toNumber(state === null || state === void 0 ? void 0 : state.supportHoursOfOperationUntilHour);
  const untilMinute = toNumber(state === null || state === void 0 ? void 0 : state.supportHoursOfOperationUntilMinute);
  if ([fromHour, fromMinute, untilHour, untilMinute].some(n => Number.isNaN(n))) {
    // Do not show an error until all four time fields are chosen
    return undefined;
  }
  const fromTotalMinutes = fromHour * 60 + fromMinute;
  const untilTotalMinutes = untilHour * 60 + untilMinute;

  // Same-day only and at least 60 minutes after start
  if (untilTotalMinutes - fromTotalMinutes < MIN_SUPPORT_DURATION_MINUTES) {
    return 'Support time until End time needs to be at least an hour after your beginning time';
  }
  return undefined;
};

// Ensure at least one support day is selected when support hours are provided
export const validateSupportDaysSelected = (_value, formState) => {
  const state = formState;
  const provideSupportHours = Boolean(state === null || state === void 0 ? void 0 : state.provideSupportHours);
  if (!provideSupportHours) {
    return undefined;
  }
  const anyDaySelected = SUPPORT_DAYS.some(d => Boolean(state === null || state === void 0 ? void 0 : state[d.name]));
  return anyDaySelected ? undefined : 'Please provide at least one day';
};

// ----------------------------
// Submit error handling helpers
// ----------------------------

// Maps backend error paths to UI field names used in this form
const vendorErrorPathToUiField = {
  // Vendor details
  '/vendor/name': 'vendorName',
  name: 'vendorName'
};
export const mapVendorCreateErrorsToFields = errors => {
  if (!errors || errors.length === 0) return {};
  return errors.reduce((acc, err) => {
    var _vendorErrorPathToUiF;
    const key = err.path ? (_vendorErrorPathToUiF = vendorErrorPathToUiField[err.path]) !== null && _vendorErrorPathToUiF !== void 0 ? _vendorErrorPathToUiF : err.path : undefined;
    if (key && err.message) acc[key] = err.message;
    return acc;
  }, {});
};
export const buildFormErrorMessage = (status, messages, fallbackMessages) => {
  var _fallbackMessages$byS, _fallbackMessages$gen;
  const first = messages && messages.length > 0 ? messages[0] : undefined;
  if (first) return first;
  if (status && fallbackMessages !== null && fallbackMessages !== void 0 && (_fallbackMessages$byS = fallbackMessages.byStatus) !== null && _fallbackMessages$byS !== void 0 && _fallbackMessages$byS[status]) return fallbackMessages.byStatus[status];
  return (_fallbackMessages$gen = fallbackMessages === null || fallbackMessages === void 0 ? void 0 : fallbackMessages.generic) !== null && _fallbackMessages$gen !== void 0 ? _fallbackMessages$gen : 'An unknown error occurred';
};